import type { FeedbackContext, FeedbackPoint } from '@/types/Models/Feedback'

const compactText = (value: string | null): string => (value || '').replace(/\s+/g, ' ').trim().slice(0, 240)

export const feedbackPath = (): string => {
  const url = new URL(window.location.href)
  for (const key of [...url.searchParams.keys()]) {
    if (/token|password|secret|key|auth|code/i.test(key)) url.searchParams.delete(key)
  }
  return url.pathname + url.search
}

export const feedbackText = (element: Element): string => {
  // Never capture form values, password fields, or editable content.
  if (element.closest('input, textarea, select, [contenteditable="true"]')) {
    return compactText(element.getAttribute('aria-label') || element.getAttribute('placeholder') || element.tagName)
  }
  const clone = element.cloneNode(true) as Element
  clone.querySelectorAll('input, textarea, select, [contenteditable], script, style, [hidden], [aria-hidden="true"]').forEach((child) => child.remove())
  return compactText(element.getAttribute('aria-label') || clone.textContent || element.getAttribute('alt'))
}

const selectorFor = (element: Element): string => {
  const parts: string[] = []
  let current: Element | null = element
  while (current && current !== document.body) {
    if (current.id && !/BootstrapVueNext|^vs\d|^v-/.test(current.id)) {
      parts.unshift(`#${CSS.escape(current.id)}`)
      break
    }
    const tag = current.tagName.toLowerCase()
    const siblings: Element[] = current.parentElement
      ? Array.from(current.parentElement.children).filter((sibling) => sibling.tagName === current?.tagName)
      : []
    parts.unshift(`${tag}:nth-of-type(${siblings.indexOf(current) + 1})`)
    current = current.parentElement
  }
  return parts.join(' > ')
}

export const captureFeedbackContext = (element: Element, x: number, y: number): FeedbackContext => {
  const rect = element.getBoundingClientRect()
  const section = element.closest('section, article, main, form')
  const heading = section?.querySelector('h1, h2, h3')
  return {
    path: feedbackPath(),
    title: document.title,
    selector: selectorFor(element),
    tag: element.tagName.toLowerCase(),
    text: feedbackText(element),
    heading: compactText(heading?.textContent || document.title),
    rowText: element.closest('tr') ? feedbackText(element.closest('tr')!) : '',
    viewport: { width: window.innerWidth, height: window.innerHeight },
    point: { x, y },
    offset: {
      x: Math.max(0, Math.min(1, (x - rect.left) / (rect.width || 1))),
      y: Math.max(0, Math.min(1, (y - rect.top) / (rect.height || 1))),
    },
    scroll: { x: window.scrollX, y: window.scrollY },
  }
}

export const findFeedbackTarget = (context: FeedbackContext): Element | null => {
  const matchesContext = (element: Element): boolean => feedbackText(element) === context.text &&
    (!context.rowText || (element.closest('tr') !== null && feedbackText(element.closest('tr')!) === context.rowText))
  try {
    const element = document.querySelector(context.selector)
    if (element && matchesContext(element)) return element
    // Sorting can move a row. Only relocate by text when the match is unique.
    if (context.text && /^[a-z][a-z0-9-]*$/.test(context.tag)) {
      const matches = Array.from(document.querySelectorAll(context.tag)).filter(
        (candidate) => !candidate.closest('.feedback') && matchesContext(candidate),
      )
      if (matches.length === 1) return matches[0] || null
    }
  } catch {
    return null
  }
  return null
}

export const feedbackPosition = (context: FeedbackContext): FeedbackPoint | null => {
  const element = findFeedbackTarget(context)
  if (!element) return null
  const rect = element.getBoundingClientRect()
  if (!rect.width || !rect.height) return null
  return { x: rect.left + rect.width * context.offset.x, y: rect.top + rect.height * context.offset.y }
}
